import { AwardProgressBadge } from "../../../../../common/components/AwardProgressBadge/AwardProgressBadge";
import { LegsProgress } from "../../PerformanceTile/LegsProgress/LegsProgress";
import { PerformanceTile } from "../../PerformanceTile/PerformanceTile";
import { PerformanceTileHeader } from "../../PerformanceTile/PerformanceTileHeader/PerformanceTileHeader";
import { PinRequirement } from "../../PerformanceTile/PinRequirement/PinRequirement";
import { PVRequirementTile } from "../../PerformanceTile/PVRequirementTile/PVRequirementTile";
import { LeafDonutWithIcon } from "../../../../../common/leaf/LeafDonutWithIcon/LeafDonutWithIcon";
import useWindowSize from "../../../../../common/hooks/useWindowSize";
import breakpoints from "../../../../../common/scss/breakpoints.module.scss";
import { useAppSelector } from "../../../../../store";
import { FetchLeadershipTripQualData } from "./LeadershipTripQualDataLayer";
import { useTranslation } from "react-i18next";
import defaultContent from "../../../../../content/performanceIncomeDashboard";
import lsiDefaultContent from "../../../../../content/leadershipSeminar";
import type { LsiRequirement } from "../../../../../services/AnnualIncomeDashboard/annualIncomeDashboardAPI.types";
import { ROUTE_PATHS } from "../../../../../common/enums/routing-enums";
import LeafRectangleLoader from "../../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { getCanonicalTitle } from "../../../../../common/util/Incentives/IncentiveRegistry";
import { QUALIFICATION_STATUS } from "../../../../../common/enums/incentive-enums";
import { ActionCardError } from "../../../GroupManagementDashboard/ActionCard/ActionCardError/ActionCardError";
import { parseBadgeStatus, parseStatus } from "../utils";

export const LeadershipTripQualTile = () => {
  const { selectedPeriod } = useAppSelector((state) => state.period);
  const { user } = useAppSelector((state) => state.boot);
  const showData = useAppSelector((state) => state.showData.showData);
  const { leadershipTripQualData, isLoading, errorStatus, refetch } = FetchLeadershipTripQualData(selectedPeriod);
  const { t } = useTranslation(["performanceIncomeDashboard", "leadershipSeminar", "navigation"]);
  const pageTitle = getCanonicalTitle({ id: "leadershipSeminar" }, t, { aff: user.aff });
  const { width } = useWindowSize();
  const isMobile = Number(width) <= Number(breakpoints["tabletSmall"]);

  const normalizeContentKey = (key: string) => {
    switch (key) {
      case "ytdVcsPercentageCheckForleadershipTrainingSeminar":
        return "ytdVcsPercentageOfPpv";
      case "ruby-optional":
        return "annualRuby";
      default:
        return key;
    }
  };

  const getQMonthRequirementContent = (formattedProgress: string): string => {
    const topLevelContent = lsiDefaultContent["qMonthRequirement"] as { title: string };
    return t("qMonthRequirement.title", topLevelContent.title, {
      ns: "leadershipSeminar",
      qs: formattedProgress,
    }).toString();
  };

  const getTitleValuesContent = (key: string, requirement: LsiRequirement, formattedProgress: string): string => {
    const topLevelDefault = lsiDefaultContent[key as keyof typeof lsiDefaultContent] as string | undefined;
    if (!topLevelDefault) return formattedProgress;

    const interpolationValues =
      requirement.titleValues!.length >= 2
        ? { val1: requirement.titleValues![0], val2: requirement.titleValues![1] }
        : { val: requirement.titleValues![0] };

    return t(key, topLevelDefault, {
      ns: "leadershipSeminar",
      ...interpolationValues,
    }).toString();
  };

  const getRequirementContent = (requirement: LsiRequirement) => {
    if (!showData) return "--";

    const key = normalizeContentKey(requirement.contentKey);
    const formattedProgress =
      key === "newFqs" || key === "qMonthRequirement"
        ? requirement.target
        : `${requirement.current}/${requirement.target}`;

    if (key === "qMonthRequirement") {
      return getQMonthRequirementContent(formattedProgress);
    }

    if (requirement.titleValues && requirement.titleValues.length > 0) {
      return getTitleValuesContent(key, requirement, formattedProgress);
    }

    const requirementContent = t(`requirements.${key}`, {
      ns: "leadershipSeminar",
      returnObjects: true,
      defaultValue: lsiDefaultContent["requirements"][key],
    }) as { title?: string } | string | undefined;

    const title = typeof requirementContent === "object" ? requirementContent?.title : undefined;

    if (!title) return formattedProgress;

    const translatedTitle = t(`requirements.${key}.title`, title, {
      volume: formattedProgress,
      volumeRuby: formattedProgress,
      volumeAnnualPapv: formattedProgress,
      group: formattedProgress,
      volumeAnnualGpv: formattedProgress,
      volumeMonthlyGpv: formattedProgress,
      newFqs: formattedProgress,
      qs: formattedProgress,
      currentVcs: requirement.current,
      Target: requirement.target,
      ns: "leadershipSeminar",
    }).toString();

    if (requirement.contentKey === "maintainLSVolume") {
      const dashboardTitle = t(
        `requirements.maintainLSVolume.dashboardTitle`,
        lsiDefaultContent["requirements"]["maintainLSVolume"]["dashboardTitle"] as string,
        { ns: "leadershipSeminar" },
      ).toString();
      return `${formattedProgress} ${dashboardTitle}`;
    }

    return translatedTitle;
  };

  const getMessageKey = (status: string) => {
    switch (status) {
      case QUALIFICATION_STATUS.HELD:
        return "onHoldMessage";
      case QUALIFICATION_STATUS.DENIED:
        return "notEligibleMessage";
      case QUALIFICATION_STATUS.MANUALLY_APPROVED:
      case QUALIFICATION_STATUS.AUTOMATICALLY_APPROVED:
        return "manuallyApprovedMessage";
      default:
        return "";
    }
  };

  if (isLoading) {
    return (
      <PerformanceTile>
        <PerformanceTileHeader label={<LeafRectangleLoader isLoading width="8rem" height="1.5rem" margin="0" />} />
        <LegsProgress
          items={[
            {
              label: <LeafRectangleLoader isLoading width="8rem" height="1.25rem" margin="0" />,
              value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
            },
            {
              label: <LeafRectangleLoader isLoading width="10rem" height="1.25rem" margin="0" />,
              value: <LeafRectangleLoader isLoading width="4rem" height="1.25rem" margin="0" />,
            },
          ]}
        />
      </PerformanceTile>
    );
  }

  if (errorStatus) {
    return (
      <PerformanceTile>
        <PerformanceTileHeader link={ROUTE_PATHS.LEADERSHIP_SEMINAR} label={pageTitle} />
        <ActionCardError onRetry={refetch} />
      </PerformanceTile>
    );
  }

  const requirements = leadershipTripQualData?.lsiTracking?.requirements;
  const pvRequirements = requirements?.filter((req) => req.type !== "PinRequirement");
  const pinRequirements = requirements?.filter((req) => req.type === "PinRequirement");

  const currentYtdOrBaseline = (contentKey: string) => {
    return contentKey === "currentYtd"
      ? t(
          "performance.leadershipSeminar.currentYtd",
          defaultContent["performance"]["leadershipSeminar"]["currentYtd"] as string,
        ).toString()
      : t("legsProgress.baseline", defaultContent["legsProgress"]["baseline"] as string).toString();
  };

  const formattedQualType = (qualType: string, value: string) => {
    switch (qualType) {
      case "frontlineQ":
        return t("currentFqs", lsiDefaultContent["currentFqs"] as string, {
          fqs: value,
          ns: "leadershipSeminar",
        }).toString();
      case "personalQ":
        return t("amountOfPQs", lsiDefaultContent["amountOfPQs"] as string, {
          val: value,
          ns: "leadershipSeminar",
        }).toString();
      case "qMonth":
        return t("legsProgress.qMonth", defaultContent["legsProgress"]["qMonth"] as string, { value }).toString();
      default:
        return value;
    }
  };

  const requirementsBody = () => {
    return (
      <>
        {leadershipTripQualData.lsiTracking.baseline && leadershipTripQualData.lsiTracking.tracking && (
          <>
            <LegsProgress
              items={[
                {
                  label: currentYtdOrBaseline(leadershipTripQualData.lsiTracking.baseline.contentKey),
                  value: showData
                    ? formattedQualType(
                        leadershipTripQualData.lsiTracking.baseline.qualType,
                        leadershipTripQualData.lsiTracking.baseline.formatted,
                      )
                    : "--",
                  ...(showData
                    ? {
                        trend: leadershipTripQualData.lsiTracking.baseline.moMTrend.trend,
                        number: leadershipTripQualData.lsiTracking.baseline.moMTrend.difference,
                      }
                    : {}),
                },
                {
                  label: t("legsProgress.tracking", defaultContent["legsProgress"]["tracking"] as string).toString(),
                  value: showData
                    ? formattedQualType(
                        leadershipTripQualData.lsiTracking.tracking.qualType,
                        leadershipTripQualData.lsiTracking.tracking.formatted,
                      )
                    : "--",
                  ...(showData
                    ? {
                        trend: leadershipTripQualData.lsiTracking.tracking.moMTrend.trend,
                        number: leadershipTripQualData.lsiTracking.tracking.moMTrend.difference,
                      }
                    : {}),
                },
              ]}
            />
            <hr />
          </>
        )}

        {pvRequirements &&
          pvRequirements.map((req) => {
            if (req.variant === "legs") {
              return (
                <div key={req.contentKey} className="pv-requirement-tile">
                  <div className="pv-requirement-tile__donut">
                    <LeafDonutWithIcon
                      size={isMobile ? "sm" : "md"}
                      isMet={showData && req.met}
                      variant="legs"
                      achievedSegments={showData ? (req.achievedSegments ?? (Number(req.current) || 0)) : 0}
                      totalSegments={showData ? (req.totalSegments ?? (Number(req.target) || 0)) : 0}
                    />
                  </div>
                  <div className="pv-requirement-tile__content">
                    <div className="pv-requirement-tile__label">{getRequirementContent(req)}</div>
                  </div>
                </div>
              );
            }
            return (
              <PVRequirementTile
                key={req.contentKey}
                percent={showData ? req.percent : 0}
                met={showData && req.met}
                label={getRequirementContent(req)}
              />
            );
          })}
        {pinRequirements &&
          pinRequirements.map((req) => (
            <PinRequirement
              key={req.contentKey}
              met={showData && req.met}
              status={showData ? parseBadgeStatus(req.status ?? "") : parseBadgeStatus("")}
              label={getRequirementContent(req)}
              value={showData ? "" : "--"}
            />
          ))}
      </>
    );
  };

  const bannerMessage = () => {
    const key = getMessageKey(leadershipTripQualData.lsiTracking.status);
    return (
      <p className="AnnualPerformanceIncome__tileSubMessage">
        {t(`performance.leadershipSeminar.${key}`, defaultContent["performance"]["leadershipSeminar"][key] as string, {
          title: pageTitle,
        })}
      </p>
    );
  };

  const comingSoonBlock = () => (
    <div className="AnnualPerformanceIncome__comingSoon">
      <p className="AnnualPerformanceIncome__comingSoon-title">
        {String(t("incentives.comingSoon.title", defaultContent.incentives.comingSoon.title))}
      </p>
      <p className="AnnualPerformanceIncome__comingSoon-message">
        {String(t("incentives.comingSoon.message", defaultContent.incentives.comingSoon.message))}
      </p>
    </div>
  );

  const isComingSoon = leadershipTripQualData.lsiTracking.isComingSoon;

  return leadershipTripQualData.lsiTracking.showLSI ? (
    <PerformanceTile>
      <PerformanceTileHeader link={ROUTE_PATHS.LEADERSHIP_SEMINAR} label={pageTitle} tealiumTag="leadershipSeminar" />
      {isComingSoon
        ? comingSoonBlock()
        : showData &&
            [
              QUALIFICATION_STATUS.HELD.toString(),
              QUALIFICATION_STATUS.DENIED.toString(),
              QUALIFICATION_STATUS.MANUALLY_APPROVED.toString(),
              QUALIFICATION_STATUS.AUTOMATICALLY_APPROVED.toString(),
            ].includes(leadershipTripQualData.lsiTracking.status)
          ? bannerMessage()
          : requirementsBody()}
      {!isComingSoon && showData && (
        <div className="perf-tile__badge">
          <AwardProgressBadge status={parseStatus(leadershipTripQualData.lsiTracking.status)} hideIcon />
        </div>
      )}
    </PerformanceTile>
  ) : null;
};
